import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';

@Injectable({
  providedIn: 'root',
})
export class GoBackService {
  constructor(
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
  ) {}

  /**
   * Navega hacia atrás usando lógica jerárquica según el rol y contexto
   */
  goBackHierarchical(): void {
    const currentUser = this.sessionService.getCurrentUser();
    if (!currentUser) {
      this.location.back();
      return;
    }

    const url = this.route.snapshot.url;
    const pathSegments = url.map((segment) => segment.path);

    // Para SUPER_ADMIN
    if (currentUser.role === UserRole.ADMIN) {
      // Verificar si estamos en nivel de partners o subpaginas de partners (create, edit, etc.)
      if (this.isAtPartnersLevel(pathSegments)) {
        // Estamos en /admin/countries/:countryId/partners o /admin/countries/:countryId/partners/create, etc.
        const countryId = this.getRouteParam('countryId');
        if (countryId) {
          this.router.navigate(['/', 'admin', 'countries', countryId]);
          return;
        }
      }
      // Verificar si estamos en nivel de beneficiaries o subpaginas
      if (this.isAtBeneficiariesLevel(pathSegments)) {
        // Estamos en /admin/countries/:countryId/partners/:partnerId/beneficiaries o subpaginas
        const countryId = this.getRouteParam('countryId');
        const partnerId = this.getRouteParam('partnerId');
        if (countryId && partnerId) {
          this.router.navigate(['/', 'admin', 'countries', countryId, 'partners']);
          return;
        }
      }
    }

    // Para COUNTRY_ADMIN
    if (currentUser.role === UserRole.COUNTRY_ADMIN) {
      if (this.isAtBeneficiariesLevel(pathSegments)) {
        // Estamos en /admin/countries/:countryId/partners/:partnerId/beneficiaries o subpaginas -> volver a partners
        const countryId = this.getRouteParam('countryId');
        const partnerId = this.getRouteParam('partnerId');
        if (countryId && partnerId) {
          this.router.navigate(['/', 'admin', 'countries', countryId, 'partners']);
          return;
        }
      }
      // Si está en partners o cualquier otro lugar, volver al home del country admin
      if (currentUser.country?.id) {
        this.router.navigate(['/', 'admin', 'countries', currentUser.country.id, 'partners']);
        return;
      }
    }

    // Para PARTNER_USER
    if (currentUser.role === UserRole.PARTNER_USER) {
      // Si está en beneficiaries o cualquier otro lugar, volver al home del partner user
      if (currentUser.country?.id && currentUser.partner?.id) {
        this.router.navigate([
          '/',
          'admin',
          'countries',
          currentUser.country.id,
          'partners',
          currentUser.partner.id,
          'beneficiaries',
        ]);
        return;
      }
    }

    // Fallback al comportamiento original
    this.location.back();
  }

  /**
   * Método original que usa history.back()
   */
  goBack(): void {
    this.location.back();
  }

  /**
   * Verifica si estamos en un nivel específico de la jerarquía
   */
  private isAtLevel(level: string, pathSegments: string[]): boolean {
    return pathSegments.includes(level);
  }

  /**
   * Verifica si estamos en el nivel de partners o cualquier subpágina de partners
   * Ej: /admin/countries/:countryId/partners, /admin/countries/:countryId/partners/create, etc.
   */
  private isAtPartnersLevel(pathSegments: string[]): boolean {
    const partnersIndex = pathSegments.indexOf('partners');
    return partnersIndex !== -1 && partnersIndex < pathSegments.length - 1;
  }

  /**
   * Verifica si estamos en el nivel de beneficiaries o cualquier subpágina de beneficiaries
   * Ej: /admin/countries/:countryId/partners/:partnerId/beneficiaries, /admin/countries/:countryId/partners/:partnerId/beneficiaries/create, etc.
   */
  private isAtBeneficiariesLevel(pathSegments: string[]): boolean {
    const beneficiariesIndex = pathSegments.indexOf('beneficiaries');
    return beneficiariesIndex !== -1 && beneficiariesIndex < pathSegments.length - 1;
  }

  /**
   * Obtiene un parámetro de la ruta recorriendo toda la jerarquía
   */
  private getRouteParam(param: string): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const value = route.snapshot.paramMap.get(param);
      if (value) return value;
      route = route.parent;
    }
    return null;
  }

  navigateTo(url: string): void {
    this.router
      .navigate([url])
      .then((success) => {
        if (success) {
          // Navegación exitosa
        } else {
          // Navegación fallida o cancelada
        }
      })
      .catch((error) => {
        console.error('Error durante la navegación:', error);
      });
  }
}
